export const APP_ROLES = ["admin", "launcher", "reader"] as const;

export type AppRole = (typeof APP_ROLES)[number];

export interface AuthIdentity {
  subject: string;
  email?: string;
  displayName?: string;
}

export interface AuthCapabilities {
  canViewAnalyst: boolean;
  canLaunch: boolean;
  canOperate: boolean;
}

const APP_ROLE_SET: ReadonlySet<string> = new Set(APP_ROLES);

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === "object";
}

function isAppRole(value: unknown): value is AppRole {
  return typeof value === "string" && APP_ROLE_SET.has(value);
}

function decodePayload(accessToken: string): Record<string, unknown> | null {
  const payload = accessToken.split(".")[1];
  if (!payload) return null;
  try {
    const padded = payload.replace(/-/g, "+").replace(/_/g, "/");
    const json = atob(padded.padEnd(Math.ceil(padded.length / 4) * 4, "="));
    const value: unknown = JSON.parse(json);
    return isRecord(value) ? value : null;
  } catch {
    return null;
  }
}

/**
 * Read only the application roles understood by both fin-api and this SPA.
 * Unknown Cognito groups must not accidentally become frontend privileges.
 */
export function recognizedRolesFromAccessToken(
  accessToken: string | null,
): AppRole[] {
  if (!accessToken) return [];
  const groups = decodePayload(accessToken)?.["cognito:groups"];
  if (
    !Array.isArray(groups) ||
    !groups.every((group) => typeof group === "string")
  ) {
    return [];
  }
  const recognized = new Set<AppRole>();
  for (const group of groups) {
    if (isAppRole(group)) recognized.add(group);
  }
  return APP_ROLES.filter((role) => recognized.has(role));
}

export function authCapabilities(
  authEnabled: boolean,
  roles: readonly AppRole[],
): AuthCapabilities {
  // The supported local-development posture is an open backend. Treat that
  // explicit posture as admin so engineers can exercise the full workspace.
  if (!authEnabled) {
    return { canViewAnalyst: true, canLaunch: true, canOperate: true };
  }
  const roleSet = new Set(roles);
  const canOperate = roleSet.has("admin");
  return {
    canViewAnalyst:
      canOperate || roleSet.has("reader") || roleSet.has("launcher"),
    canLaunch: canOperate || roleSet.has("launcher"),
    canOperate,
  };
}

export function securityPrincipalKey(
  identity: AuthIdentity | null,
  roles: readonly AppRole[],
): string {
  return `${identity?.subject ?? "anonymous"}|${[...roles].sort().join(",")}`;
}

export function identityFromProfile(profile: unknown): AuthIdentity | null {
  if (!isRecord(profile)) return null;
  const subject = typeof profile.sub === "string" ? profile.sub : "";
  if (!subject) return null;
  const email = typeof profile.email === "string" ? profile.email : undefined;
  const displayName =
    typeof profile.name === "string"
      ? profile.name
      : typeof profile.preferred_username === "string"
        ? profile.preferred_username
        : undefined;
  return { subject, email, displayName };
}
